import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Card,
  Box,
  Dialog as SubDialog,
  DialogActions as SubDialogActions,
  DialogTitle as SubDialogTitle,
  DialogContent as SubDialogContent,
  TextField,
  Avatar,
  Collapse,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import handleMultipleWasteInventory from "../Functions/HandleMultipleWasteInventory";

const MultipleWasteDialog = ({ open, onClose, token, onWasteComplete }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [wasteCart, setWasteCart] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [confirmView, setConfirmView] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchMenus();
      setWasteCart([]);
      setConfirmView(false);
    }
  }, [open]);

  const fetchMenus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/fetch-menu-with-archive`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const allItems = [
        ...(response.data.menu?.menuItems || []),
        ...(response.data.archive?.menuArchive || []),
      ];

      const parsed = allItems.map((m) => ({
        id: m._id || m.menuId,
        name: m.menuName,
        image: m.menuImage || "",
        category: m.categoryName || "Uncategorized",
        ingredients: m.menuIngredients || [],
        expanded: false,
      }));

      setMenuItems(parsed);
    } catch (err) {
      console.error("Failed to fetch menu:", err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = () => {
    if (!quantity || isNaN(quantity) || Number(quantity) <= 0) return;
    setWasteCart((prev) => [
      ...prev,
      {
        menuId: selectedMenu.id,
        menuName: selectedMenu.name,
        quantity: Number(quantity),
      },
    ]);
    setSelectedMenu(null);
    setQuantity("");
  };

  const handleSubmitWaste = async () => {
    await handleMultipleWasteInventory(wasteCart, token, () => {
      onWasteComplete();
      onClose();
    });
  };

  const removeFromCart = (index) => {
    setWasteCart((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleExpand = (id) => {
    setMenuItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, expanded: !item.expanded } : item
      )
    );
  };

  const filteredItems = menuItems.filter((item) => {
    const term = search.toLowerCase();
    return (
      (item.name || "").toLowerCase().includes(term) ||
      (item.category || "").toLowerCase().includes(term)
    );
  });

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {confirmView ? "Confirm Waste Entries" : "Menu Waste"}
        </DialogTitle>

        <DialogContent dividers>
          {confirmView ? (
            <>
              {wasteCart.length === 0 ? (
                <Typography>No items in waste cart.</Typography>
              ) : (
                <Grid container spacing={2}>
                  {wasteCart.map((item, index) => {
                    const matched = menuItems.find((m) => m.id === item.menuId);
                    return (
                      <Grid item xs={12} key={index}>
                        <Card
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            padding: 2,
                            justifyContent: "space-between",
                            gap: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Avatar
                              variant="rounded"
                              src={matched?.image || ""}
                              alt={item.menuName}
                              sx={{ width: 64, height: 64 }}
                            />
                            <Typography variant="subtitle1">
                              {item.menuName}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <TextField
                              label="Qty"
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => {
                                const updated = [...wasteCart];
                                updated[index].quantity = Number(
                                  e.target.value
                                );
                                setWasteCart(updated);
                              }}
                              sx={{ width: 80 }}
                            />
                            <Button
                              size="small"
                              color="error"
                              onClick={() => removeFromCart(index)}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </>
          ) : (
            <>
              <TextField
                label="Search Menu"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2 }}
              />

              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 4,
                  }}
                >
                  <Typography variant="body1" gutterBottom>
                    Fetching Menu...
                  </Typography>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{ maxHeight: 500, overflowY: "auto" }}
                >
                  {filteredItems.map((item) => (
                    <Grid item xs={12} key={item.id}>
                      <Card
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          padding: 2,
                          gap: 2,
                        }}
                      >
                        <Avatar
                          variant="rounded"
                          src={item.image}
                          alt={item.name}
                          sx={{ width: 64, height: 64 }}
                        />

                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.category}
                          </Typography>

                          <Collapse in={item.expanded}>
                            <Box sx={{ mt: 1 }}>
                              {item.ingredients.length > 0 ? (
                                item.ingredients.map((ing, idx) => (
                                  <Typography
                                    key={idx}
                                    variant="body2"
                                    sx={{ pl: 2 }}
                                  >
                                    - {ing.ingredientName} ({ing.amount})
                                  </Typography>
                                ))
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ pl: 2 }}
                                >
                                  No ingredients listed.
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Button
                            variant="outlined"
                            onClick={() => setSelectedMenu(item)}
                          >
                            Waste
                          </Button>
                          <Button
                            size="small"
                            onClick={() => toggleExpand(item.id)}
                          >
                            {item.expanded
                              ? "Hide Ingredients"
                              : "Show Ingredients"}
                          </Button>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          {!confirmView && <Button onClick={onClose}>Close</Button>}
          <Button
            variant="outlined"
            onClick={() => setConfirmView(!confirmView)}
            disabled={wasteCart.length === 0}
          >
            {confirmView ? "Back" : `View Cart (${wasteCart.length})`}
          </Button>
          {confirmView && wasteCart.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                setLoading(true);
                await handleSubmitWaste();
                setLoading(false);
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Confirm Waste"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Quantity prompt dialog */}
      <SubDialog
        open={Boolean(selectedMenu)}
        onClose={() => setSelectedMenu(null)}
      >
        <SubDialogTitle>Waste Quantity for {selectedMenu?.name}</SubDialogTitle>
        <SubDialogContent>
          <TextField
            label="Quantity to Waste"
            fullWidth
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            autoFocus
            sx={{ mt: 1 }}
          />
        </SubDialogContent>
        <SubDialogActions>
          <Button onClick={() => setSelectedMenu(null)}>Cancel</Button>
          <Button variant="contained" onClick={addToCart}>
            Add to Cart
          </Button>
        </SubDialogActions>
      </SubDialog>
    </>
  );
};

export default MultipleWasteDialog;
